import type { DesignItem } from '../types';

const BG_REMOVER_KEY = 'backgroundRemover';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Recolored markup for an SVG layer, as a data URL.
 *
 * The runtime `src` of a recolored item is a blob URL owned by the document that
 * created it, so it is dead in every other page — the product mockup renderer, the
 * cart thumb, the storefront quick personalizer. Falling through to `thumb`/`file`
 * there silently draws the untouched source artwork, losing the recolor.
 */
function svgMarkupDataUrl(item: DesignItem): string {
  if (item.type !== 'svg') return '';
  const markup = typeof item.svg === 'string' ? item.svg : '';
  if (!markup.includes('<svg')) return '';
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
}

/**
 * Pick the best source URL for pipeline rendering.
 * Prefers transparent PNG from background remover, then recolored vector markup.
 */
export function resolveImageSourceUrls(item: DesignItem): string[] {
  const ex = item as Record<string, unknown>;
  const urls: string[] = [];
  const push = (u: unknown) => {
    const s = String(u ?? '').trim();
    if (s && !urls.includes(s)) urls.push(s);
  };

  const bgState = ex[BG_REMOVER_KEY];
  if (isRecord(bgState)) {
    push(bgState.resultSrc);
    push(bgState.processedSrc);
  }

  push(svgMarkupDataUrl(item));

  push(ex.medium);
  push(item.src);
  push(ex.large);
  push(item.thumb);
  push(ex.file);

  return urls;
}

export function resolveImageSourceUrl(item: DesignItem): string {
  return resolveImageSourceUrls(item)[0] ?? '';
}
